import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import SectionBadge from "@/components/shared/Title";
import { FaCalculator } from "react-icons/fa";
import EmployeeSelector from "./EmployeeSelector";
import PlanSelector from "./PlanSelector";
import PriceSummary from "./PriceSummary";
import Button from "@/components/shared/Button";
import { FiZap, FiCalendar } from "react-icons/fi";

import MotionDiv from "@/components/animations/MotionDiv";
import { fadeUp, scaleUp, stagger } from "@/animations/variants";

import { usePackages } from "@/hooks/usePackages";

const DEFAULT_EMPLOYEES = 5;

const CALCULATOR_PLANS = [
  {
    id: "monthly",
    label: "monthly",
    billedKey: "pricing.billed_monthly",
  },
  {
    id: "yearly",
    label: "yearly",
    billedKey: "pricing.billed_annual",
  },
  {
    id: "halfYear",
    label: "halfYear",
    billedKey: "pricing.billed_semiAnnual",
  },
];

const PricingCalculatorSection = () => {
  const { t, i18n } = useTranslation();

  // لازم يكون هنا داخل الـ component
  const navigate = useNavigate();

  const isAR = i18n.language?.startsWith("ar");

  const [employees, setEmployees] = useState(DEFAULT_EMPLOYEES);
  const [planId, setPlanId] = useState("monthly");

  const {
    groupedPlans,
    employeeCounts,
    loading,
    error,
  } = usePackages(isAR);

  const activePlan = useMemo(() => {
    if (!employeeCounts.length) return null;

    const matchedEmployees =
      employeeCounts.find((count) => employees <= count) ||
      employeeCounts[employeeCounts.length - 1];

    const packageGroup = groupedPlans[matchedEmployees];

    const pricePerEmployee = Number(
      packageGroup?.pricing?.[planId] || 0
    );

    return {
      id: planId,
      employees,
      employeesLimit: matchedEmployees,
      price: pricePerEmployee,
      total: employees * pricePerEmployee,
    };
  }, [
    employees,
    planId,
    employeeCounts,
    groupedPlans,
  ]);

  const handleBookDemo = () => {
    navigate("/book-demo");
  };

  return (
    <section className="py-10">
      {/* Title */}
      <MotionDiv
        variants={fadeUp}
        className="mb-10 text-center"
      >
        <SectionBadge
          text={t("pricing.calculator.title")}
          icon={<FaCalculator />}
        />
      </MotionDiv>

      {/* Calculator */}
      <MotionDiv
        variants={scaleUp}
        className="mx-4 max-w-5xl rounded-2xl bg-white p-6 shadow lg:mx-4 xl:mx-auto"
      >
        {loading ? (
          <div className="py-10 text-center text-gray-500">
            Loading...
          </div>
        ) : error ? (
          <div className="py-10 text-center text-red-500">
            {error}
          </div>
        ) : (
          <>
            <MotionDiv variants={stagger}>
              {/* Employees */}
              <MotionDiv variants={fadeUp}>
                <EmployeeSelector
                  count={employees}
                  setCount={setEmployees}
                  t={t}
                />
              </MotionDiv>

              {/* Plans */}
              <MotionDiv variants={fadeUp}>
                <PlanSelector
                  plans={CALCULATOR_PLANS}
                  active={planId}
                  onSelect={setPlanId}
                  t={t}
                />
              </MotionDiv>

              {/* Price */}
              <MotionDiv variants={fadeUp}>
                <PriceSummary
                  employees={employees}
                  plan={activePlan}
                  t={t}
                />
              </MotionDiv>
            </MotionDiv>

            {/* Buttons */}
            <MotionDiv
              variants={fadeUp}
              className="mt-6 flex flex-col gap-3 sm:flex-row"
            >
              {/* احجز عرض */}
              <Button
                type="button"
                onClick={handleBookDemo}
                variant="primary"
                className="w-full"
                rightIcon={<FiZap />}
              >
                {t("pricing.cta")}
              </Button>

              {/* تجربة مجانية */}
              <Button
                type="button"
                onClick={handleBookDemo}
                variant="secondary"
                className="w-full"
                rightIcon={<FiCalendar />}
              >
                {t("pricing.trial7days") || "7 Days Free Trial"}
              </Button>
            </MotionDiv>

            {/* Note */}
            <MotionDiv variants={fadeUp}>
              <p className="mt-3 text-center text-xs text-green-600">
                {t("pricing.calculator.yearlyDiscountNote")}
              </p>
            </MotionDiv>
          </>
        )}
      </MotionDiv>
    </section>
  );
};

export default PricingCalculatorSection;